import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCloudIPs } from '../api';

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const ThContext = React.createContext({});

const Th = ({ label, sortKey }) => {
  const { sortConfig, colFilters, onSort, onFilterChange } = React.useContext(ThContext);
  return (
    <th className="bg-white align-top p-1" style={{ whiteSpace: 'nowrap', zIndex: 10 }}>
      <div
        onClick={() => sortKey && onSort(sortKey)}
        className="d-flex align-items-center justify-content-between text-dark mb-1"
        style={{ cursor: sortKey ? 'pointer' : 'default', fontSize: '0.75rem', fontWeight: '700' }}
      >
        <span className="me-2">{label}</span>
        {sortKey && sortConfig.key === sortKey && (
          <i className={`bi bi-chevron-${sortConfig.direction === 'asc' ? 'up' : 'down'} text-primary`} style={{ fontSize: '0.65rem' }} />
        )}
        {sortKey && sortConfig.key !== sortKey && (
          <i className="bi bi-arrow-down-up text-muted opacity-25" style={{ fontSize: '0.65rem' }} />
        )}
      </div>
      {sortKey && (
        <input
          type="text"
          className="form-control form-control-sm border-secondary shadow-none bg-light"
          style={{ fontSize: '0.75rem', padding: '0.1rem 0.3rem', height: '24px', minWidth: '70px' }}
          placeholder="Fil.."
          value={colFilters[sortKey] || ''}
          onChange={e => onFilterChange(sortKey, e.target.value)}
        />
      )}
    </th>
  );
};

const COLUMNS = [
  { key: 'SERVERIP',      label: 'Server IP' },
  { key: 'SUBTYPE',       label: 'Subscription Type' },
  { key: 'BLOCK',         label: 'Status' },
  { key: 'HOSTNAME',      label: 'Host Name' },
  { key: 'CONSOLEKVM_IP', label: 'Console IP' },
  { key: 'DCLOC',         label: 'DC Location' },
  { key: 'RAM',           label: 'RAM' },
  { key: 'CORE',          label: 'CPU' },
  { key: 'SSD',           label: 'DISK' },
  { key: 'UPDATEDBY',     label: 'Updated By' },
];

const pgBtnStyle = (active, disabled) => ({
  minWidth: 32, height: 32, padding: '0 8px',
  borderRadius: 7, border: active ? 'none' : '1px solid #dee2e6',
  background: active ? 'linear-gradient(135deg,#4f46e5,#6366f1)' : disabled ? '#f8f9fa' : '#fff',
  color: active ? '#fff' : disabled ? '#adb5bd' : '#495057',
  fontWeight: active ? 700 : 500, fontSize: '0.82rem',
  cursor: disabled ? 'not-allowed' : 'pointer',
  boxShadow: active ? '0 2px 8px rgba(99,102,241,0.35)' : 'none',
  transition: 'all 0.15s',
});

/* ─────────────────────────────────────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────────────────────────────────────── */
export default function CloudServerIP() {
  const navigate = useNavigate();

  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const [colFilters, setColFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize]       = useState(15);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError('');
    getCloudIPs()
      .then(r => setAllRows(r.data || []))
      .catch(e => setError(e?.response?.data?.message || 'Failed to load data.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSort = (key) => {
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  const handleFilterChange = (key, val) => {
    setColFilters(prev => ({ ...prev, [key]: val }));
  };

  const filtered = React.useMemo(() => {
    let result = [...allRows];
    Object.keys(colFilters).forEach(key => {
      const v = colFilters[key]?.toLowerCase();
      if (v) result = result.filter(r => String(r[key] || '').toLowerCase().includes(v));
    });
    if (sortConfig.key) {
      result.sort((a, b) => {
        const vA = a[sortConfig.key] ?? '', vB = b[sortConfig.key] ?? '';
        const nA = parseFloat(vA), nB = parseFloat(vB);
        if (!isNaN(nA) && !isNaN(nB)) return sortConfig.direction === 'asc' ? nA - nB : nB - nA;
        return sortConfig.direction === 'asc' ? String(vA).localeCompare(String(vB)) : String(vB).localeCompare(String(vA));
      });
    }
    return result;
  }, [allRows, sortConfig, colFilters]);

  useEffect(() => { setCurrentPage(1); }, [sortConfig, colFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalRows  = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const pagedRows  = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const pageNumbers = React.useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1].filter(p => p >= 1 && p <= totalPages));
    const sorted = [...pages].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('…');
      result.push(sorted[i]);
    }
    return result;
  }, [totalPages, currentPage]);

  const hasActiveFilters = Object.values(colFilters).some(v => v);

  /* ────────────────────────────────────────────────────────────────────────
     RENDER
  ──────────────────────────────────────────────────────────────────────── */
  return (
    <div className="py-2">

      {/* ── Toolbar ── */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <h5 className="fw-bold text-dark mb-0 me-2">
          <i className="bi bi-cloud-check-fill me-2 text-primary" />Cloud Server IP
        </h5>

        <span className="badge bg-light text-secondary border px-2 py-1 fw-medium" style={{ fontSize: '0.78rem' }}>
          {totalRows !== allRows.length
            ? <>{totalRows} <span className="text-muted">/ {allRows.length} records</span></>
            : <>{allRows.length} records</>}
        </span>

        <div className="ms-auto d-flex gap-2">
          {hasActiveFilters && (
            <button className="btn btn-sm btn-outline-danger fw-semibold" onClick={() => setColFilters({})}>
              <i className="bi bi-x-circle me-1" />Clear Filters
            </button>
          )}
          <button className="btn btn-sm btn-outline-primary bg-light fw-semibold" onClick={fetchData}>
            <i className="bi bi-arrow-clockwise me-1" />Refresh
          </button>
          <button className="btn btn-sm btn-secondary" onClick={() => navigate('/server-management')}>
            <i className="bi bi-arrow-left me-1" />Back
          </button>
        </div>
      </div>

      {/* ── Error ── */}
      {error && <div className="alert alert-danger py-2 fw-medium rounded-3 mb-3">{error}</div>}

      {/* ── Table ── */}
      {loading ? (
        <div className="text-center py-5 my-5">
          <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} />
          <div className="mt-3 text-secondary fw-medium">Loading records...</div>
        </div>
      ) : allRows.length === 0 ? (
        <div className="text-center text-secondary py-5 border rounded bg-white">
          <i className="bi bi-inbox fs-1 text-muted opacity-50 d-block mb-2 mt-3" />
          <p className="fw-medium mb-3">No data available.</p>
        </div>
      ) : (
        <>
        <div className="table-responsive shadow-sm border rounded" style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}>
          <table className="table table-hover table-sm align-middle mb-0" style={{ fontSize: '0.8rem' }}>
            <ThContext.Provider value={{ sortConfig, colFilters, onSort: handleSort, onFilterChange: handleFilterChange }}>
            <thead className="sticky-top shadow-sm" style={{ zIndex: 11 }}>
              <tr>
                {COLUMNS.map(col => <Th key={col.key} label={col.label} sortKey={col.key} />)}
              </tr>
            </thead>
            </ThContext.Provider>
            <tbody>
              {pagedRows.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="text-center text-muted py-4">
                    No records match the current filters.
                  </td>
                </tr>
              ) : (
                pagedRows.map((row, idx) => (
                  <tr key={idx} style={{ whiteSpace: 'nowrap' }}>
                    {COLUMNS.map(col => (
                      <td key={col.key} className="p-1">
                        {row[col.key] === null || row[col.key] === undefined ? '' : String(row[col.key])}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination bar ── */}
        {totalRows > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 12, padding: '12px 16px',
            background: '#f8f9ff', borderRadius: '0 0 8px 8px',
            border: '1px solid #dee2e6', borderTop: 'none',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.82rem', color: '#6c757d' }}>
              <span>
                Showing&nbsp;
                <strong style={{ color: '#212529' }}>{(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, totalRows)}</strong>
                &nbsp;of&nbsp;<strong style={{ color: '#212529' }}>{totalRows}</strong>&nbsp;records
              </span>
              <span style={{ color: '#dee2e6' }}>|</span>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 0 }}>
                Rows&nbsp;per&nbsp;page
                <select
                  value={pageSize}
                  onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                  style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: '2px 6px', fontSize: '0.8rem', background: '#fff', color: '#212529', cursor: 'pointer' }}
                >
                  {[10, 15, 25, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} style={pgBtnStyle(false, currentPage === 1)} title="Previous page">
                <i className="bi bi-chevron-left" style={{ fontSize: '0.75rem' }} />
              </button>
              {pageNumbers.map((p, i) =>
                p === '…'
                  ? <span key={`e-${i}`} style={{ padding: '0 4px', color: '#adb5bd', fontSize: '0.85rem', userSelect: 'none' }}>…</span>
                  : <button key={p} onClick={() => setCurrentPage(p)} style={pgBtnStyle(p === currentPage, false)}>{p}</button>
              )}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} style={pgBtnStyle(false, currentPage === totalPages)} title="Next page">
                <i className="bi bi-chevron-right" style={{ fontSize: '0.75rem' }} />
              </button>
            </div>

            <div style={{ fontSize: '0.82rem', color: '#6c757d' }}>
              Page&nbsp;<strong style={{ color: '#212529' }}>{currentPage}</strong>&nbsp;of&nbsp;<strong style={{ color: '#212529' }}>{totalPages}</strong>
            </div>
          </div>
        )}
        </>
      )}
    </div>
  );
}
